/**
 * Provider-specific widget iframe adapters.
 *
 * Each provider that uses checkout.method === 'widget' can define:
 *   - buildUrl:      custom iframe URL construction
 *   - onLoad:        post-load messages (e.g. whitelist handshake)
 *   - iframeAttrs:   custom iframe allow / sandbox attributes
 *
 * CheckoutStep calls these via the gateway-agnostic resolve helpers at the
 * bottom of this file.  To onboard a new provider, add an entry to the
 * `adapters` map — no changes needed in the component.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface WidgetAdapter {
	/** Build the iframe src URL from the checkout base URL + widgetConfig. */
	buildUrl(baseUrl: string, config: Record<string, unknown>): string;

	/** Called after the iframe fires `onLoad`. */
	onLoad?(iframe: HTMLIFrameElement, widgetOrigin: string): void;

	/** Extra iframe `allow` attribute value. Falls back to a safe default. */
	iframeAllow?: string;

	/** Iframe `sandbox` attribute value. Omitted when undefined. */
	iframeSandbox?: string;
}

// ---------------------------------------------------------------------------
// Wert adapter
// Mirrors @wert-io/widget-initializer SDK behaviour so the whitelist check
// and URL routing work identically to a native SDK integration.
// @see node_modules/@wert-io/widget-initializer/index.ts
// ---------------------------------------------------------------------------

const wert: WidgetAdapter = {
	buildUrl(baseUrl, config) {
		// SDK format: {origin}/{partner_id}/widget?key=val&…
		const partnerId = config.partner_id as string | undefined;
		const path = partnerId ? `/${partnerId}/widget` : '/widget';
		const url = new URL(path, baseUrl);

		for (const [key, value] of Object.entries(config)) {
			// SDK excludes origin, partner_id, and object values from query params
			if (key === 'origin' || key === 'partner_id') continue;
			if (value == null || value === '' || typeof value === 'object') continue;
			url.searchParams.set(key, String(value));
		}
		url.searchParams.set('widget_layout_mode', 'Modal');
		return url.toString();
	},

	onLoad(iframe, widgetOrigin) {
		// SDK sends parent-hostname so Wert can validate the embedding domain
		iframe.contentWindow?.postMessage(
			{ type: 'parent-hostname', data: { hostname: window.location.hostname } },
			widgetOrigin,
		);
		iframe.contentWindow?.postMessage(
			{ type: 'allow-redirect', data: { redirectAllowed: false } },
			widgetOrigin,
		);
	},

	iframeAllow: 'camera *; microphone *; payment',
	iframeSandbox: 'allow-scripts allow-forms allow-popups allow-same-origin',
};

// ---------------------------------------------------------------------------
// Registry — add new providers here
// ---------------------------------------------------------------------------

const adapters: Record<string, WidgetAdapter> = {
	wert,
};

// ---------------------------------------------------------------------------
// Public resolve helpers (used by CheckoutStep)
// ---------------------------------------------------------------------------

const defaultAdapter: WidgetAdapter = {
	buildUrl(baseUrl, config) {
		const url = new URL(baseUrl);
		for (const [key, value] of Object.entries(config)) {
			if (value == null || value === '' || typeof value === 'object') continue;
			url.searchParams.set(key, String(value));
		}
		return url.toString();
	},
};

/** Resolve the widget adapter for a gateway, falling back to a generic one. */
export function getWidgetAdapter(gateway: string): WidgetAdapter {
	return adapters[gateway] ?? defaultAdapter;
}
